import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface ItemSelecao {
  valor: string;
  rotulo: string;
  detalhe?: string;
}

/**
 * Select no visual novo (login v2a). A lista abre dentro do proprio fluxo da tela, e nao
 * flutuando: dentro de modal com overflow escondido um painel absoluto seria cortado.
 * Busca opcional (lista de unidades e longa) e teclado: setas, Enter e Esc.
 */
@Component({
  selector: 'app-lista-selecao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './lista-selecao.component.html',
  styleUrl: './lista-selecao.component.scss',
})
export class ListaSelecaoComponent {
  @Input() itens: ItemSelecao[] = [];
  @Input() valor: string | null = null;
  @Input() placeholder = 'Selecione';
  @Input() desabilitado = false;
  @Input() comBusca = false;
  @Input() rotuloAcessivel = '';
  // Busca também no detalhe (ex.: especialidade do médico).
  @Input() buscarNoDetalhe = false;
  // Valor usado no ultimo acesso: aparece fixado no topo enquanto nao ha busca.
  @Input() ultimaUsada: string | null = null;
  @Output() escolher = new EventEmitter<string>();

  @ViewChild('campoBusca') campoBusca?: ElementRef<HTMLInputElement>;
  @ViewChild('gatilho') gatilho?: ElementRef<HTMLButtonElement>;

  aberto = false;
  termo = '';
  indice = 0;

  constructor(private host: ElementRef<HTMLElement>) {}

  get selecionado(): ItemSelecao | undefined {
    return this.itens.find(i => i.valor === this.valor);
  }

  get fixada(): ItemSelecao | undefined {
    if (!this.ultimaUsada || this.normalizar(this.termo)) return undefined;
    return this.itens.find(i => i.valor === this.ultimaUsada);
  }

  get filtrados(): ItemSelecao[] {
    const t = this.normalizar(this.termo);
    if (t) return this.itens.filter(i => this.normalizar(i.rotulo).includes(t)
      || (this.buscarNoDetalhe && this.normalizar(i.detalhe ?? '').includes(t)));
    const fixada = this.fixada;
    return fixada ? [fixada, ...this.itens.filter(i => i !== fixada)] : this.itens;
  }

  private normalizar(s: string): string {
    return (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  alternar(): void {
    if (this.desabilitado) return;
    this.aberto ? this.fechar() : this.abrir();
  }

  abrir(): void {
    this.aberto = true;
    this.termo = '';
    const atual = this.itens.findIndex(i => i.valor === this.valor);
    this.indice = atual >= 0 ? atual : 0;
    setTimeout(() => this.campoBusca?.nativeElement.focus());
  }

  fechar(devolverFoco = false): void {
    this.aberto = false;
    if (devolverFoco) setTimeout(() => this.gatilho?.nativeElement.focus());
  }

  escolherItem(item: ItemSelecao): void {
    this.escolher.emit(item.valor);
    this.fechar(true);
  }

  teclaGatilho(e: KeyboardEvent): void {
    if (!this.aberto && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      this.abrir();
    } else if (this.aberto) {
      this.teclaLista(e);
    }
  }

  teclaLista(e: KeyboardEvent): void {
    const total = this.filtrados.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.indice = total ? (this.indice + 1) % total : 0;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.indice = total ? (this.indice - 1 + total) % total : 0;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = this.filtrados[this.indice];
      if (item) this.escolherItem(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.fechar(true);
    }
  }

  // O pai monta os itens de novo a cada ciclo; sem trackBy o botao sob o mouse era
  // recriado entre o mousedown e o mouseup e o clique nunca chegava.
  porValor(_: number, item: ItemSelecao): string {
    return item.valor;
  }

  onBusca(): void {
    this.indice = 0;
  }

  @HostListener('document:mousedown', ['$event'])
  cliqueFora(e: MouseEvent): void {
    if (this.aberto && !this.host.nativeElement.contains(e.target as Node)) this.fechar();
  }
}
